import { createTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

// Direction-E "Classic Blue" — formal, flat, single-hue. Replaces the
// vibrant indigo/pink gradient direction: one conservative blue carries
// every brand surface (buttons, links, active states, focus rings), no
// second "accent" hue, no gradients anywhere. Secondary is a plain
// neutral slate rather than a competing color, kept only because MUI's
// component API expects a secondary palette slot. Neutrals stay on the
// cool slate scale — reads as a mature enterprise/corporate dashboard.
const colors = {
  primary: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB', // Classic blue — brand anchor
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
  },
  // Neutral stand-in, not a second brand hue — used sparingly (e.g. a
  // muted "secondary" button) so nothing competes with primary blue.
  secondary: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  success: {
    50: '#ECFDF5',
    100: '#D1FAE5',
    200: '#A7F3D0',
    300: '#6EE7B7',
    400: '#34D399',
    500: '#10B981',
    600: '#059669',
    700: '#047857',
    800: '#065F46',
    900: '#064E3B',
  },
  warning: {
    50: '#FFFBEB',
    100: '#FEF3C7',
    200: '#FDE68A',
    300: '#FCD34D',
    400: '#FBBF24',
    500: '#F59E0B',
    600: '#D97706',
    700: '#B45309',
    800: '#92400E',
    900: '#78350F',
  },
  error: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444',
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
  },
  neutral: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
};

// Flat, neutral-gray shadow stack — no colored glow. Formal elevation:
// a soft dark shadow, not a brand-tinted one.
const flatShadow = {
  rest: '0 1px 2px rgba(15, 23, 42, 0.05)',
  raised: '0 4px 12px rgba(15, 23, 42, 0.08)',
  hover: '0 10px 28px rgba(15, 23, 42, 0.10)',
  modal: '0 28px 56px rgba(15, 23, 42, 0.20)',
};

export const elegantTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary[600],
      light: colors.primary[400],
      dark: colors.primary[800],
      contrastText: '#ffffff',
    },
    secondary: {
      main: colors.secondary[600],
      light: colors.secondary[400],
      dark: colors.secondary[800],
      contrastText: '#ffffff',
    },
    success: { main: colors.success[600], light: colors.success[400], dark: colors.success[800] },
    warning: { main: colors.warning[500], light: colors.warning[400], dark: colors.warning[700] },
    error: { main: colors.error[600], light: colors.error[400], dark: colors.error[800] },
    background: {
      default: colors.neutral[50],
      paper: '#FFFFFF',
    },
    text: {
      primary: colors.neutral[800],
      secondary: colors.neutral[500],
    },
    divider: colors.neutral[200],
  },
  typography: {
    fontFamily:
      '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 800,
      lineHeight: 1.15,
      letterSpacing: '-0.02em',
      color: colors.neutral[900],
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.015em',
      color: colors.neutral[900],
    },
    h3: {
      fontSize: '1.625rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: '-0.01em',
      color: colors.neutral[800],
    },
    h4: {
      fontSize: '1.375rem',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.005em',
      color: colors.neutral[800],
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.35,
      color: colors.neutral[800],
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: colors.neutral[800],
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.55,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 500,
      letterSpacing: '0.02em',
      lineHeight: 1.4,
      color: colors.neutral[500],
    },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: colors.neutral[500],
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    flatShadow.rest,
    flatShadow.raised,
    flatShadow.hover,
    flatShadow.hover,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
    flatShadow.modal,
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.neutral[50],
          fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '9px 20px',
          fontSize: '0.875rem',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 160ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        // Flat, solid CTA — no gradient. Hover just steps one shade
        // darker, the conventional formal-UI pattern.
        contained: {
          backgroundColor: colors.primary[600],
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: colors.primary[700],
          },
          '&.Mui-disabled': {
            backgroundColor: colors.neutral[200],
            color: colors.neutral[400],
          },
        },
        containedSecondary: {
          backgroundColor: colors.secondary[600],
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: colors.secondary[700],
          },
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: colors.neutral[300],
          color: colors.neutral[800],
          '&:hover': {
            borderWidth: 1.5,
            borderColor: colors.primary[400],
            backgroundColor: alpha(colors.primary[500], 0.05),
          },
        },
        text: {
          color: colors.primary[700],
          '&:hover': {
            backgroundColor: alpha(colors.primary[500], 0.06),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: flatShadow.rest,
          border: `1px solid ${colors.neutral[200]}`,
          backgroundColor: '#FFFFFF',
          transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: flatShadow.raised,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: flatShadow.rest,
          border: `1px solid ${colors.neutral[200]}`,
          backgroundColor: '#FFFFFF',
          backgroundImage: 'none',
        },
        elevation1: { boxShadow: flatShadow.rest },
        elevation2: { boxShadow: flatShadow.raised },
        elevation3: { boxShadow: flatShadow.hover },
      },
    },
    // Applied once at the OutlinedInput level (not just under
    // MuiTextField) so plain <Select variant="outlined"> filters get
    // the exact same rounded/hover/focus treatment as text fields.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: '#FFFFFF',
          transition: 'all 160ms cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.neutral[300],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.primary[400],
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: colors.primary[600],
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px ${alpha(colors.primary[500], 0.12)}`,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: colors.primary[700],
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 26,
          letterSpacing: '0.01em',
        },
        filled: {
          backgroundColor: colors.neutral[100],
          color: colors.neutral[700],
        },
        outlined: {
          borderColor: colors.neutral[300],
          backgroundColor: '#FFFFFF',
        },
        colorPrimary: {
          backgroundColor: colors.primary[100],
          color: colors.primary[800],
        },
        colorSecondary: {
          backgroundColor: colors.secondary[200],
          color: colors.secondary[800],
        },
        colorSuccess: {
          backgroundColor: colors.success[100],
          color: colors.success[800],
        },
        colorWarning: {
          backgroundColor: colors.warning[100],
          color: colors.warning[800],
        },
        colorError: {
          backgroundColor: colors.error[100],
          color: colors.error[700],
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: 'all 160ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: alpha(colors.primary[500], 0.08),
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          backgroundColor: '#FFFFFF',
          borderRight: `1px solid ${colors.neutral[200]}`,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.neutral[200]}`,
          background: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(12px)',
          color: colors.neutral[800],
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${colors.neutral[200]}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colors.neutral[100],
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: colors.neutral[700],
            fontSize: '0.75rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: colors.neutral[200],
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: alpha(colors.primary[500], 0.03),
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          height: 2,
          backgroundColor: colors.primary[600],
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minHeight: 44,
          '&.Mui-selected': {
            color: colors.primary[700],
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: flatShadow.modal,
        },
      },
    },
  },
});

// Utility — semantic tokens that pages can opt-in to without reaching
// into the theme object directly.
export const glassboxTokens = {
  surface: {
    linen: colors.neutral[50],
    linen2: colors.neutral[100],
    paper: '#FFFFFF',
  },
  text: {
    coffee: colors.neutral[800],
    coffeeMid: colors.neutral[500],
    coffeeSoft: colors.neutral[400],
    espresso: colors.neutral[900],
  },
  border: {
    stone: colors.neutral[200],
  },
  shadow: flatShadow,
  // Kept as a no-op alias (all solid colors, no actual gradients) so
  // call sites that reference glassboxTokens.gradient.* still resolve
  // instead of needing a per-file sweep.
  gradient: {
    brand: colors.primary[600],
    brandDeep: colors.primary[700],
    brandSoft: alpha(colors.primary[500], 0.08),
    accent: colors.secondary[600],
    success: colors.success[600],
  },
  // For money/KPI numbers — tabular nums + slight tracking adjustment
  // keeps Rp values column-aligned across rows.
  tabularNum: {
    fontFeatureSettings: '"tnum" 1, "lnum" 1',
    fontVariantNumeric: 'tabular-nums lining-nums',
  } as const,
  mono: {
    fontFamily: '"JetBrains Mono", "Menlo", "Monaco", monospace',
  } as const,
};

export default elegantTheme;
